import { FC, useCallback, useEffect, useState } from "react";
import { format, isSameDay, parseISO, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";
import { uk } from "date-fns/locale/uk";

import { styled } from "@mui/material/styles";
import { DateCalendar } from "@mui/x-date-pickers";
import { useTheme } from "@mui/material";

const StyledCalendar = styled(DateCalendar)(({ theme }) => {
  return {
    "&.MuiDateCalendar-root": {
      width: "100%",

      "& .MuiPickersCalendarHeader-root": {
        margin: 0,
        padding: theme.spacing(1.75, 2.75),
        textTransform: "capitalize",
        position: "relative",

        "& .MuiPickersArrowSwitcher-root": {
          width: 0,
        },
        "& .MuiPickersCalendarHeader-labelContainer": {
          margin: "auto",
          fontWeight: theme.typography.fontWeightRegular,

          "& .MuiPickersCalendarHeader-label": {
            margin: 0,
          },
        },
        "& .MuiIconButton-edgeEnd": {
          position: "absolute",
          left: 0,
          top: 7,
          bottom: 0,
          color: theme.palette.secondary.main,

          "&.Mui-disabled": {
            color: theme.palette.secondary.dark,
          },
        },
        "& .MuiIconButton-edgeStart": {
          position: "absolute",
          right: 0,
          top: 7,
          bottom: 0,
          color: theme.palette.secondary.main,

          "&.Mui-disabled": {
            color: theme.palette.secondary.dark,
          },
        },
      },

      "& .MuiDayCalendar-root": {
        padding: theme.spacing(1.75),
      },

      "& .MuiDayCalendar-weekContainer, & .MuiDayCalendar-header": {
        justifyContent: "space-between",
      },

      "& .MuiDayCalendar-weekContainer": {
        "& .MuiButtonBase-root": {
          width: 30,
          height: 34,
          fontSize: 14,
          margin: theme.spacing(0.5),
          fontWeight: theme.typography.fontWeightMedium,
          borderRadius: 0,

          "&:hover, &:focus, &:active": {
            outline: "none",
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: theme.palette.success.main,
            color: theme.palette.success.main,
          },

          "&.Mui-selected": {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.text.secondary,
            "&.MuiPickersDay-dayOutsideMonth": {
              color: theme.palette.text.secondary,
            },
          },

          "&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected:active": {
            color: theme.palette.text.secondary,
          },

          "&.Mui-disabled": {
            color: theme.palette.primary.light,
          },

          "&.MuiPickersDay-dayOutsideMonth": {
            color: theme.palette.text.primary,
          },

          "&.Mui-disabled.MuiPickersDay-dayOutsideMonth": {
            color: theme.palette.primary.light,
          },
        },
      },

      "& .MuiDayCalendar-header": {
        "& .MuiTypography-root.MuiTypography-caption": {
          color: theme.palette.primary.contrastText,
          fontWeight: theme.typography.fontWeightMedium,
          textTransform: "capitalize",
          fontSize: 14,
        },
      },
    },
  };
});

type CalendarProps = {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  availableDates?: string[];
  disablePast?: boolean;
  enableWeekSelection?: boolean;
};

const Calendar: FC<CalendarProps> = ({ value, onChange, availableDates, disablePast, enableWeekSelection }) => {
  const [selectedWeek, setSelectedWeek] = useState<{ start: Date; end: Date } | null>(null);
  const { palette } = useTheme();

  const shouldDisableDate = useCallback(
    (date: any) => {
      if (!availableDates) return false;

      const parsedDates = availableDates.map((dateStr) => parseISO(dateStr));

      for (const availableDate of parsedDates) {
        if (isSameDay(date, availableDate)) {
          return false;
        }
      }
      return true;
    },
    [availableDates],
  );

  useEffect(() => {
    if (value){
      const startOfSelectedWeek = startOfWeek(value, { weekStartsOn: 1 }); // Week starts on Monday (1)
      const endOfSelectedWeek = endOfWeek(value, { weekStartsOn: 1 });

      setSelectedWeek({ start: startOfSelectedWeek, end: endOfSelectedWeek });
    }
  }, [value]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Calculate the start and end of the week for the selected date
      const startOfSelectedWeek = startOfWeek(date, { weekStartsOn: 1 }); // Week starts on Monday (1)
      const endOfSelectedWeek = endOfWeek(date, { weekStartsOn: 1 });

      setSelectedWeek({ start: startOfSelectedWeek, end: endOfSelectedWeek });
    }

    // Pass the selected date to the parent component
    onChange(date);
  };

  // Function to check if a day is within the selected week
  const isDayInSelectedWeek = (date: Date) => {
    if (!selectedWeek) return false;
    return isWithinInterval(date, { start: selectedWeek.start, end: selectedWeek.end });
  };

  return (
    <StyledCalendar
      shouldDisableDate={shouldDisableDate}
      value={value}
      onChange={enableWeekSelection ? handleDateChange : onChange}
      dayOfWeekFormatter={(d) =>
        format(d as any, "E", {
          locale: uk,
        })
      }
      disablePast={disablePast}
      views={["day"]}
      showDaysOutsideCurrentMonth
      slotProps={enableWeekSelection ? {
        day: props => {
          return {
            sx: {
              ...(isDayInSelectedWeek(props.day) && {
                backgroundColor: palette.success.main,
                color: `${palette.primary.main} !important`,
              }),
            },
          }
        }
      } : undefined}
    />
  );
};

export default Calendar;
